<template>
  <ValidationObserver ref="observer"
                      v-slot="{invalid}"
                      tag="form"
                      @submit.prevent="submit()">
    <div class="dp02 p-3 mt-3">
      <div class="row">
        <div class="col-9">
          <h2 v-if="!isEdit"
              class="my-4">
            {{ $t("CreateEditGenotypeCombinations.HeaderIsNotEdit") }}
          </h2>
          <h2 v-else
              class="my-4">
            {{ $t("CreateEditGenotypeCombinations.HeaderIsEdit") }}
          </h2>
        </div>
        <div class="col-3">
          <p>
            <kbd class="key">←</kbd>&nbsp;
            <kbd class="key">↑</kbd>&nbsp;
            <kbd class="key">→</kbd>&nbsp;
            <kbd class="key">↓</kbd>&nbsp;{{ $t("CreateEditGenotypeCombinations.Navigating") }}
          </p>
          <p>
            <kbd class="key">Ctrl  ←</kbd>&nbsp;
            <kbd class="key">Ctrl  →</kbd>&nbsp;
            {{ $t("CreateEditGenotypeCombinations.Copy") }}
          </p>
        </div>
      </div>
      <b-table
        ref="table"
        striped
        borderless
        outlined
        dark
        responsive
        :items="genes"
        :fields="fields">
        <template v-slot:thead-top="data">
          <b-tr>
            <b-th v-if="numberOfCombinations > 1" />
            <b-th v-if="numberOfCombinations >= 1" />
            <template v-if="numberOfCombinations > 2">
              <b-th v-for="index in numberOfCombinations - 2"
                    :key="index">
                <b-button
                  size="xs"
                  variant="danger"
                  @click="removeCombination(index + 1)">
                  -
                </b-button>
              </b-th>
            </template>

            <b-th>
              <b-button
                v-if="numberOfCombinations > 1"
                size="xs"
                variant="danger"
                style="float:left"
                @click="removeCombination(numberOfCombinations + 1)">
                -
              </b-button>
              <b-button
                size="xs"
                variant="success"
                style="float:right"
                @click="increaseNumberOfCombinations">
                +
              </b-button>
            </b-th>
          </b-tr>
        </template>
        <template
          v-for="index in numberOfCombinations"
          v-slot:[`cell(${index})`]="row">
          <ValidationProvider
            :key="index"
            :name="row.item['Name'] + ' combination number '+index"
            rules="required"
            v-slot="{errors, classes}">
            <b-form-input
              :ref="row.item['Name']+'_'+index"
              v-model.trim="row.item[index]"
              @keydown.ctrl.arrow-left="copyLeft(index)"
              @keydown.ctrl.arrow-right="copyRight(index)"
              @keydown.arrow-left="navigateLeft(row.item['Name'], index)"
              @keydown.arrow-right="navigateRight(row.item['Name'], index)"
              @keydown.arrow-down="navigateDown(row.item['Name'], index)"
              @keydown.arrow-up="navigateUp(row.item['Name'], index)"
              style="min-width:5vw"
              :class="nonUniqueCombinations.includes(index)
                ? 'myInputInvalid' : {...classes, myInput: true}"
              type="text" />
          </ValidationProvider>
        </template>
      </b-table>
      <div>
        <template v-if="$refs.observer">
          <p class="errorMsg"
             v-for="x in validationErrors"
             :key="x[0]">
            {{ x[0] }}
          </p>
        </template>
        <template v-if="equals !== undefined && equals.length !== 0">
          <p class="errorMsg"
             v-for="x in equals"
             :key="x[0]">
            {{ $t("CreateEditGenotypeCombinations.EqualError", {1 : x[1], 2: x[2]}) }}
          </p>
        </template>
        <template v-if="serverValidationErrors.length !== 0">
          <p class="errorMsg"
             v-for="(error, i) in serverValidationErrors"
             :key="i">
            {{ error }}
          </p>
        </template>
      </div>
      <div>
        <b-button
          type="submit"
          variant="success myButton align-middle">
          {{ $t("common.NextStep") }}
        </b-button>
        <b-spinner
          v-if="validating"
          variant="primary"
          class="align-middle ml-3"
          label="Spinning" />
      </div>
    </div>
  </ValidationObserver>
</template>
<script>
import { mapMutations, mapGetters, mapState, mapActions } from 'vuex';

export default {
  name: 'CreateEditGenotypeCombinations',
  data() {
    return {
      numberOfCombinations: 1,
      genes: [],
      fields: [{ key: 'Name', stickyColumn: true }, '1'],
      isEdit: false,
      validating: false,
      equals: [],
      serverValidationErrors: []
    };
  },
  computed: {
    ...mapState('createTemplate', ['genetypeCombinationsNotUnique']),
    nonUniqueCombinations() {
      return Object.keys(this.genetypeCombinationsNotUnique).filter((x) => this.genetypeCombinationsNotUnique[x]).map((x) => Number(x));
    },
    validationErrors() {
      return this.$refs.observer.ctx.errors;
    }
  },
  mounted() {
    this.isEdit = this.getTemplateId() !== null;
    if (this.getGenetypeCombinations().length === 0) {
      this.genes = this.getGeneCombination()
        .name.split(',')
        .map((x) => ({ Name: x.trim(), 1: '' }));
    } else {
      this.numberOfCombinations = this.getNumberOfCombinations();
      for (let i = 2; i <= this.numberOfCombinations; i += 1) {
        this.fields.push(`${i}`);
      }
      this.genes = this.getGenetypeCombinations();
    }
  },
  methods: {
    increaseNumberOfCombinations() {
      this.numberOfCombinations += 1;
      this.setNumberOfCombinations(this.numberOfCombinations);
      for (let i = 0; i < this.genes.length; i += 1) {
        this.$set(this.genes[i], this.numberOfCombinations, '');
      }
      this.fields.push(`${this.numberOfCombinations}`);
      this.$refs.table.refresh();
    },
    removeCombination(index) {
      for (let i = 0; i < this.genes.length; i += 1) {
        delete this.genes[i][index];
        for (let j = index; j < this.numberOfCombinations; j += 1) {
          this.genes[i][j] = this.genes[i][j + 1];
        }
        this.$delete(this.genes[i], this.numberOfCombinations);
        delete this.genes[i][this.numberOfCombinations];
      }
      this.removeCombinationInvalid(index);
      this.fields.pop();
      this.numberOfCombinations -= 1;
      this.setNumberOfCombinations(this.numberOfCombinations);
      this.$refs.table.refresh();
    },
    ...mapGetters({
      getGeneCombination: 'createTemplate/getGeneCombination',
      getGenetypeCombinations: 'createTemplate/getGenetypeCombinations',
      getNumberOfCombinations: 'createTemplate/getNumberOfCombinations',
      getTemplateId: 'createTemplate/getTemplateId',
      getGenoTypeCombinationsArray: 'createTemplate/getGenoTypeCombinationsArray'
    }),
    ...mapMutations({
      setGenetypeCombinations: 'createTemplate/setGenetypeCombinations',
      setNumberOfCombinations: 'createTemplate/setNumberOfCombinations',
      removeCombinationInvalid: 'createTemplate/removeCombinationInvalid',
      setGenetypeCombinationsNotUnique: 'createTemplate/setGenetypeCombinationsNotUnique'
    }),
    ...mapActions({
      validateGenotypeCombinations: 'createTemplate/validateGenotypeCombinations'
    }),
    async submit() {
      this.equals = [];
      this.setGenetypeCombinationsNotUnique({});
      const isValid = await this.$refs.observer.validate();
      if (isValid) {
        this.setGenetypeCombinations(this.genes);
        const equal = this.checkCombinationsUnique();
        if (equal.length === 0) {
          this.serverValidationErrors = [];
          this.validateGenotypeCombinations()
            .then((response) => {
              const validationResult = response.data;
              if (validationResult.isValid) {
                this.$emit('next-step');
              } else {
                this.serverValidationErrors = validationResult.errors;
                const equalCombinationsObj = {};
                for (let i = 1; i <= this.numberOfCombinations; i += 1) {
                  equalCombinationsObj[i] = validationResult.fieldIds.includes(i);
                }
                this.setGenetypeCombinationsNotUnique(equalCombinationsObj);
              }
            });
        } else {
          this.equals = equal;
        }
      }
    },
    checkCombinationsUnique() {
      const combinations = this.getGenoTypeCombinationsArray()();
      const equal = [];
      let arrKey = 0;
      for (let i = 1; i <= combinations.length; i += 1) {
        const current = combinations[i - 1];
        for (let j = i + 1; j <= combinations.length; j += 1) {
          const compare = combinations[j - 1];
          let same = 0;
          Object.keys(current).forEach((key) => {
            if (current[key] === compare[key] || current[key] === '*' || compare[key] === '*') {
              same += 1;
            }
          });
          if (same === Object.keys(current).length) {
            equal.push([arrKey, i, j]);
            arrKey += 1;
          }
        }
      }
      if (equal.length === 0) {
        this.setGenetypeCombinationsNotUnique({});
      } else {
        const equalCombinationsArr = equal.reduce((a, b) => a.concat([b[1], b[2]]), []);
        const equalCombinationsObj = {};
        for (let i = 1; i <= this.numberOfCombinations; i += 1) {
          equalCombinationsObj[i] = equalCombinationsArr.includes(i);
        }
        this.setGenetypeCombinationsNotUnique(equalCombinationsObj);
      }
      return equal;
    },
    navigateLeft(row, index) {
      if (index === 1) return;
      const newIndex = index - 1;
      this.$refs[`${row}_${newIndex}`][0].focus();
    },
    navigateUp(row, index) {
      const arrIndex = this.genes.findIndex((x) => x.Name === row);
      if (arrIndex === 0) return;
      const gene = this.genes[arrIndex - 1];
      this.$refs[`${gene.Name}_${index}`][0].focus();
    },
    navigateRight(row, index) {
      if (index === this.numberOfCombinations) return;
      const newIndex = index + 1;
      this.$refs[`${row}_${newIndex}`][0].focus();
    },
    navigateDown(row, index) {
      const arrIndex = this.genes.findIndex((x) => x.Name === row);
      if (arrIndex === this.genes.length - 1) return;
      const gene = this.genes[arrIndex + 1];
      this.$refs[`${gene.Name}_${index}`][0].focus();
    },
    copyLeft(index) {
      if (index === 0) return;
      this.genes.forEach((x) => {
        x[index - 1] = x[index];
      });
    },
    copyRight(index) {
      if (index === this.numberOfCombinations) return;
      this.genes.forEach((x) => {
        x[index + 1] = x[index];
      });
    }
  }
};
</script>
<style lang="scss" scoped>
</style>
